import React from 'react';

import { useTranslation } from 'react-i18next';
import { M } from 'components/typography';
import { PopupMenu, TextButton } from 'design-system-web';
import type { CostOffsetCode } from 'domains-billing-types';

import { useGetActionsForCostOffsetCodes } from './useGetActionsForCostOffsetCodes';

interface TableColumn {
  key: string;
  header: string;
  renderCell: (cell: string, row: CostOffsetCode | null) => React.ReactNode;
  disableSorting: boolean;
}

interface UseGetCostOffsetCodesTableColumnsParams {
  refetch: () => Promise<void> | void;
}

function renderStringCell(cell: string, _row: CostOffsetCode | null) {
  return <M>{cell}</M>;
}

export function useGetCostOffsetCodesTableColumns({
  refetch,
}: UseGetCostOffsetCodesTableColumnsParams): TableColumn[] {
  const { t } = useTranslation();
  const actions = useGetActionsForCostOffsetCodes({ refetch });

  const actionColumn: TableColumn = {
    key: 'actions',
    header: t('abp.productsServices.costOffsetCodes.actions'),
    disableSorting: true,
    renderCell: (_cell: string, row: CostOffsetCode | null) => {
      if (!row) {
        return null;
      }

      return (
        <div data-test="cost-offset-code-actions">
          <PopupMenu
            trigger={
              <TextButton iconName="angle-down">
                {t('abp.productsServices.costOffsetCodes.actions')}
              </TextButton>
            }
            items={actions.map(({ label, onSelect }) => ({
              label,
              onSelect: () => {
                onSelect(row);
              },
              isDisabled: false,
            }))}
          />
        </div>
      );
    },
  };

  return [
    {
      key: 'code',
      header: t('abp.costOffsetCodes.code'),
      renderCell: renderStringCell,
      disableSorting: false,
    },
    {
      key: 'description',
      header: t('abp.costOffsetCodes.description'),
      renderCell: renderStringCell,
      disableSorting: true,
    },
    actionColumn,
  ];
}
